import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  IconWrapper,
  IStyles,
  pxToRem,
  pxTovW,
  theme,
} from '@geneo2-web/shared-ui';
import { ProjectGroupMarkSummary } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { useState } from 'react';

export interface IGroupDetailsProps {
  groups: ProjectGroupMarkSummary[];
  /** Marks every member of this project is graded out of. */
  maxMarks?: number;
  /** Defaults to the group-count heading; overridden for ungrouped projects. */
  heading?: string;
  /** Grade + section (e.g. "6A") shown against each member; the mark API omits it. */
  gradeSection?: string;
  onGiveMarks: (group: ProjectGroupMarkSummary) => void;
  onViewProject?: (group: ProjectGroupMarkSummary) => void;
  onViewEvidence?: (group: ProjectGroupMarkSummary) => void;
  /** Omit to render the group name as read only (no rename API yet). */
  onGroupNameChange?: (group: ProjectGroupMarkSummary, newName: string) => void;
}

const styles: IStyles = {
  root: {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: 'common.white',
    border: '1px solid #0AA34F',
    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    padding: {
      xs: `${pxToRem(14)} ${pxToRem(14)}`,
      md: `${pxToRem(16)} ${pxToRem(18)}`,
      lg: `${pxTovW(16)} ${pxTovW(20)}`,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    cursor: 'pointer',
  },
  groupList: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    marginTop: { xs: pxToRem(14), md: pxToRem(16), lg: pxTovW(16) },
  },
  groupCard: {
    border: '1px solid #E0DFDE',
    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    padding: {
      xs: `${pxToRem(10)} ${pxToRem(12)}`,
      md: `${pxToRem(10)} ${pxToRem(14)}`,
      lg: `${pxTovW(10)} ${pxTovW(14)}`,
    },
    boxSizing: 'border-box',
  },
  groupRow: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
    // Phones keep the name and chevron on top, with the actions on a row below.
    flexWrap: { xs: 'wrap', md: 'nowrap' },
    rowGap: { xs: pxToRem(10), md: 0 },
  },
  actions: {
    // On md+ the actions sit inline in the group row, as if unwrapped.
    display: { xs: 'flex', md: 'contents' },
    alignItems: 'center',
    gap: pxToRem(8),
    order: { xs: 2, md: 0 },
    flexBasis: '100%',
    paddingTop: { xs: pxToRem(10) },
    borderTop: { xs: '1px solid #E6E9EE', md: 'none' },
  },
  giveMarksWrapper: {
    flex: { xs: 1, md: 'none' },
    display: 'flex',
  },
  membersToggle: {
    order: { xs: 1, md: 0 },
  },
  groupIconBox: {
    height: { xs: pxToRem(32), md: pxToRem(28), lg: pxTovW(36) },
    width: { xs: pxToRem(32), md: pxToRem(28), lg: pxTovW(36) },
    minWidth: { xs: pxToRem(32), md: pxToRem(28), lg: pxTovW(36) },
    borderRadius: { xs: pxToRem(5), md: pxToRem(5), lg: pxTovW(5) },
    backgroundColor: '#EFF4EF',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  nameBox: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxToRem(8), lg: pxTovW(8) },
    minWidth: 0,
  },
  nameInput: {
    border: '1px solid #DBDBDB',
    borderRadius: { xs: pxToRem(6), md: pxToRem(6), lg: pxTovW(6) },
    paddingX: { xs: pxToRem(8), md: pxToRem(8), lg: pxTovW(8) },
    width: '100%',
  },
  giveMarksButton: {
    flexShrink: 0,
    width: { xs: '100%', md: 'auto' },
    height: { xs: pxToRem(36), md: 'auto' },
    textTransform: 'none',
    fontFamily: 'Lato',
    fontWeight: 700,
    color: '#0AA34F',
    border: '1px solid #0AA34F',
    backgroundColor: 'common.white',
    borderRadius: { xs: pxToRem(6), md: pxToRem(6), lg: pxTovW(6) },
    paddingX: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
    paddingY: { xs: pxToRem(4), md: pxToRem(4), lg: pxTovW(4) },
    fontSize: { xs: pxToRem(12), md: pxToRem(12), lg: pxTovW(12) },
    '&:hover': {
      backgroundColor: 'common.white',
      border: '1px solid #0AA34F',
      boxShadow: 'none',
    },
  },
  divider: {
    marginY: { xs: pxToRem(12), md: pxToRem(14), lg: pxTovW(14) },
    borderColor: '#E6E9EE',
  },
  membersHeading: {
    display: 'block',
    fontFamily: 'poppins',
    fontWeight: 400,
    color: '#1A1A1A',
    fontSize: { xs: pxToRem(16), md: pxToRem(19), lg: pxTovW(21) },
    paddingBottom: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
    borderBottom: '1px solid #E6E9EE',
  },
  membersGrid: {
    display: 'grid',
    // minmax(0, …) lets the tracks shrink below their content width;
    // plain `1fr` would stretch past the card around long member text.
    gridTemplateColumns: {
      xs: 'minmax(0, 1fr)',
      md: 'repeat(2, minmax(0, 1fr))',
    },
    columnGap: { xs: pxToRem(24), md: pxToRem(30), lg: pxTovW(40) },
    marginTop: { xs: pxToRem(6), md: pxToRem(8), lg: pxTovW(8) },
  },
  memberCard: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
    paddingY: { xs: pxToRem(12), md: pxToRem(14), lg: pxTovW(14) },
    borderBottom: '1px solid #E6E9EE',
    // Drop the trailing rule under the last row (last cell always; the
    // left cell of the final full row on the two-column layout).
    '&:last-of-type': { borderBottom: 'none' },
    '&:nth-last-of-type(2):nth-of-type(odd)': {
      borderBottom: { md: 'none' },
    },
  },
  avatar: {
    width: { xs: pxToRem(36), md: pxToRem(30), lg: pxTovW(40) },
    height: { xs: pxToRem(36), md: pxToRem(30), lg: pxTovW(40) },
    borderRadius: { xs: pxToRem(8), md: pxToRem(8), lg: pxTovW(8) },
    backgroundColor: '#3F3D91',
    color: '#FFFFFF',
    fontWeight: 700,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: { xs: pxToRem(14), md: pxToRem(16), lg: pxTovW(16) },
    flexShrink: 0,
  },
  memberInfo: {
    minWidth: 0,
  },
  memberSubText: {
    display: 'block',
    overflowWrap: 'anywhere',
    color: '#3F3D91',
    fontSize: { xs: pxToRem(12), md: pxToRem(14), lg: pxTovW(14) },
  },
};

/** Group name falls back to its position so a nameless group still reads as a group. */
const groupLabel = (group: ProjectGroupMarkSummary, index: number) =>
  group.groupName?.trim() || `Group ${index + 1}`;

/** Two-letter monogram; the API sends no avatar for a student. */
const initialsOf = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .slice(0, 2)
    .join('');

export const GroupDetails = (props: IGroupDetailsProps) => {
  const {
    groups,
    maxMarks,
    heading,
    gradeSection,
    onGiveMarks,
    onViewProject,
    onViewEvidence,
    onGroupNameChange,
  } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(true);
  const [openGroupId, setOpenGroupId] = useState<bigint>();
  const [editingGroupId, setEditingGroupId] = useState<bigint>();
  const [draftName, setDraftName] = useState('');

  const startEditing = (group: ProjectGroupMarkSummary, index: number) => {
    setEditingGroupId(group.studentGroupId);
    setDraftName(groupLabel(group, index));
  };

  const saveName = (group: ProjectGroupMarkSummary) => {
    const newName = draftName.trim();
    if (newName && newName !== group.groupName) {
      onGroupNameChange?.(group, newName);
    }
    setEditingGroupId(undefined);
  };

  /** One group's members open at a time; the panel sits in a narrow column. */
  const toggleMembers = (group: ProjectGroupMarkSummary) => {
    setOpenGroupId((previous) =>
      previous === group.studentGroupId ? undefined : group.studentGroupId
    );
  };

  return (
    <Box sx={styles.root}>
      <Box
        sx={styles.header}
        onClick={() => setExpanded((previous) => !previous)}
      >
        <Typography
          variant={isMobile ? 'h4' : 'h3'}
          fontWeight="bold"
          fontFamily="lato"
        >
          {heading ??
            `Group Details (${groups.length} Group${
              groups.length === 1 ? '' : 's'
            })`}
        </Typography>
        <KeyboardArrowDownRoundedIcon
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </Box>

      <Collapse in={expanded}>
        <Box sx={styles.groupList}>
          {groups.map((group, index) => {
            const isEditing = editingGroupId === group.studentGroupId;
            const membersOpen = openGroupId === group.studentGroupId;
            /** Marking is rejected server side until the group submits. */
            const canGiveMarks = group.isSubmitted;
            const groupMaxMarks = group.groupMaxMarks || maxMarks || 0;
            const members = group.studentMarks;
            const hasEvidence = group.userUploads.some(
              (upload) => !upload.isDeleted
            );

            return (
              <Box key={String(group.studentGroupId)} sx={styles.groupCard}>
                <Box sx={styles.groupRow}>
                  <Box sx={styles.groupIconBox}>
                    <IconWrapper
                      name="project_group"
                      parentFolder="icons"
                      type="png"
                      size="small"
                    />
                  </Box>

                  <Box sx={styles.nameBox}>
                    <Box sx={styles.nameRow}>
                      {isEditing ? (
                        <Box sx={styles.nameInput}>
                          <InputBase
                            fullWidth
                            autoFocus
                            value={draftName}
                            onChange={(event) =>
                              setDraftName(event.target.value)
                            }
                            onBlur={() => saveName(group)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                saveName(group);
                              }
                            }}
                            inputProps={{ 'aria-label': 'Edit group name' }}
                          />
                        </Box>
                      ) : (
                        <>
                          <Typography variant="bodyText" noWrap>
                            {groupLabel(group, index)}
                          </Typography>
                          {onGroupNameChange && (
                            <IconButton
                              size="small"
                              aria-label="Edit group name"
                              onClick={() => startEditing(group, index)}
                            >
                              <IconWrapper
                                name="edit"
                                parentFolder="icons"
                                type="png"
                                size="small"
                              />
                            </IconButton>
                          )}
                        </>
                      )}
                    </Box>
                    <Typography variant="subText" color="#9F9F9F">
                      {`${group.activeStudentCount} student${
                        group.activeStudentCount === 1 ? '' : 's'
                      }${group.isSubmittedLate ? ' · Submitted late' : ''}`}
                    </Typography>
                  </Box>

                  <Box sx={styles.actions}>
                    {onViewProject && group.isSubmitted && (
                      <Tooltip title="View submitted project">
                        <IconButton
                          size="small"
                          aria-label="View submitted project"
                          onClick={() => onViewProject(group)}
                        >
                          <IconWrapper
                            name="eye-black"
                            parentFolder="icons"
                            type="png"
                            size="small"
                          />
                        </IconButton>
                      </Tooltip>
                    )}

                    {/* Only offered when there is something to preview - the
                      photo and video are optional for the student. */}
                    {onViewEvidence && hasEvidence && (
                      <Tooltip title="View project evidence">
                        <IconButton
                          size="small"
                          aria-label="View project evidence"
                          onClick={() => onViewEvidence(group)}
                        >
                          <IconWrapper
                            name="file-group"
                            parentFolder="icons"
                            type="png"
                            size="small"
                          />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip
                      title={
                        canGiveMarks
                          ? ''
                          : 'Group has not submitted the project yet'
                      }
                    >
                      {/* A disabled button fires no events, so the tooltip needs a wrapper. */}
                      <Box component="span" sx={styles.giveMarksWrapper}>
                        <Button
                          variant="outlined"
                          disabled={!canGiveMarks}
                          onClick={() => onGiveMarks(group)}
                          sx={styles.giveMarksButton}
                        >
                          Give Marks
                        </Button>
                      </Box>
                    </Tooltip>
                  </Box>

                  {members.length > 0 && (
                    <IconButton
                      sx={styles.membersToggle}
                      size="small"
                      aria-label={membersOpen ? 'Hide members' : 'Show members'}
                      onClick={() => toggleMembers(group)}
                    >
                      <KeyboardArrowDownRoundedIcon
                        sx={{
                          transform: membersOpen
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  )}
                </Box>

                <Collapse in={membersOpen}>
                  <Divider sx={styles.divider} />
                  <Typography sx={styles.membersHeading}>
                    Members ({members?.length})
                  </Typography>
                  <Box sx={styles.membersGrid}>
                    {members.map((member) => {
                      const memberName =
                        member.studentName || `Student ${member.studentId}`;

                      return (
                        <Box
                          key={member.studentId.toString()}
                          sx={styles.memberCard}
                        >
                          <Box sx={styles.avatar}>{initialsOf(memberName)}</Box>
                          <Box sx={styles.memberInfo}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              noWrap
                            >
                              {memberName}
                            </Typography>
                            <Typography
                              variant="subText"
                              sx={styles.memberSubText}
                            >
                              {[
                                gradeSection,
                                member.rollNumber &&
                                  `Roll No: ${member.rollNumber}`,
                                member.isGraded &&
                                  `Marks: ${
                                    Math.round(member.obtainedMarks * 100) / 100
                                  }/${groupMaxMarks}`,
                              ]
                                .filter(Boolean)
                                .join(' | ')}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};
